/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import {
	type ComponentType,
	Fragment,
	type PropsWithChildren,
	useCallback,
	useEffect,
	useState,
} from 'react';

import { cssMap, jsx } from '@atlaskit/css';
import { Box } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import {
	SainAction,
	useSAINUsageCriteriaMet,
} from '../../common/controller/analytics/hooks/use-sain-usage';
import { useAnalytics } from '../../common/controller/analytics/use-analytics';
import { AISources } from '../../common/ui/ai-sources';
import { AIAnswerBody } from '../ai-answer-body/ai-answer-body';
import { useAIAnswerContext } from '../ai-answer-context';
import { AIAnswerFooter } from '../ai-answer-footer/ai-answer-footer';
import { AIAnswerLoadingDialog } from '../ai-answer-loading/ai-answer-loading-dialog';
import {
	aiAnswerCollapseClicked,
	aiAnswerExpandClicked,
	aiAnswerExpandShown,
	onAIAnswerSourceClick,
} from '../analytics';
import { NLPSearchResultFormat, type NLPSearchResultSource } from '../types';

import { Collapse } from './collapse';
import { AIAnswerSearchBorder } from './styled';

const styles = cssMap({
	spacer: {
		height: '14px',
		paddingTop: token('space.250'),
	},
	errorContainer: {
		marginTop: token('space.100'),
		marginBottom: token('space.200'),
	},
	sourcesContainer: {
		paddingTop: '0',
		paddingRight: token('space.300'),
		paddingBottom: token('space.100'),
		paddingLeft: token('space.300'),
	},
	scroll: {
		maxHeight: '50vh',
		overflowX: 'hidden',
		overflowY: 'auto',
		scrollbarColor: `${token('color.border.bold')} transparent`,
	},
});

const MAX_SOURCES = 4;

type LegacySmarAnswerCardProps = {
	LoadingWrapper: ComponentType<PropsWithChildren<{}>>;
	DialogWrapper: ComponentType<PropsWithChildren<{}>>;
	onFMPReached?: () => void;
	onAnswerExpandedChange?: (expanded: boolean) => void;
};

export const LegacySmartAnswerCard: ({
	LoadingWrapper,
	DialogWrapper,
	onFMPReached,
	onAnswerExpandedChange,
}: LegacySmarAnswerCardProps) => JSX.Element = ({
	LoadingWrapper,
	DialogWrapper,
	onFMPReached,
	onAnswerExpandedChange,
}: LegacySmarAnswerCardProps) => {
	const {
		commonAttributes,
		callbacks,
		loading,
		isReadingAids,
		onClose,
		hasError,
		initiallyExpanded,
	} = useAIAnswerContext();
	const { fireSAINUsageEvent } = useSAINUsageCriteriaMet();
	const { query } = commonAttributes;
	const { answerFormat } = commonAttributes;
	const [fireAnalyticsEvent] = useAnalytics();
	const [collapsed, setCollapsed] = useState(true);
	const isQuickSearch = Boolean(!isReadingAids && onClose);

	// reset on each new query
	useEffect(() => {
		setCollapsed(!initiallyExpanded);
		// Notify parent context of initial state
		onAnswerExpandedChange?.(!initiallyExpanded);
	}, [query, initiallyExpanded, onAnswerExpandedChange]);

	// Call onFMPReached when answer is loaded
	useEffect(() => {
		if (!loading && !hasError) {
			onFMPReached?.();
		}
	}, [loading, hasError, onFMPReached]);

	const toggleCollapsedState = () => {
		if (collapsed) {
			fireSAINUsageEvent({ metCriteria: SainAction.EXPAND_CLICKED, commonAttributes });
		}
		setCollapsed(!collapsed);
		// Notify parent context of state change
		onAnswerExpandedChange?.(!collapsed);
	};

	const analyticsCallbacks = {
		onExpandedByDefault: () => fireAnalyticsEvent(aiAnswerExpandShown(commonAttributes)),
		onExpandClicked: () => fireAnalyticsEvent(aiAnswerExpandClicked(commonAttributes)),
		onCollapseClicked: () => fireAnalyticsEvent(aiAnswerCollapseClicked(commonAttributes)),
	};

	const sourceClickCallback = useCallback(
		(index: number, source: NLPSearchResultSource, hostname?: string) => {
			fireAnalyticsEvent(
				onAIAnswerSourceClick({
					sourceIndex: index,
					hostname,
					sourceProduct: source.product || '',
					...commonAttributes,
				}),
			);
			fireSAINUsageEvent({
				metCriteria: SainAction.SOURCE_CLICKED,
				commonAttributes,
				sourceProduct: source.product || '',
			});
			callbacks?.onAIAnswerSourceClick?.();
		},
		[fireAnalyticsEvent, commonAttributes, fireSAINUsageEvent, callbacks],
	);

	if (loading) {
		return (
			<LoadingWrapper>
				{
					// disable collapse/expand when streaming for quick search
					isQuickSearch ? (
						<AIAnswerLoadingDialog />
					) : (
						<AIAnswerLoadingDialog
							collapsed={collapsed}
							toggleCollapsedState={toggleCollapsedState}
							callbacks={analyticsCallbacks}
						/>
					)
				}
			</LoadingWrapper>
		);
	}

	let aiAnswerContent = (
		<Collapse
			collapsed={collapsed}
			toggleCollapsedState={toggleCollapsedState}
			expandWhenClickedAnywhere
			isLoading={false}
			callbacks={analyticsCallbacks}
		>
			<AIAnswerBody />
		</Collapse>
	);

	if (hasError) {
		aiAnswerContent = (
			<Box xcss={styles.errorContainer}>
				<AIAnswerBody />
			</Box>
		);
	} else if (isQuickSearch || answerFormat === NLPSearchResultFormat.JSON) {
		aiAnswerContent = (
			<Fragment>
				<Box xcss={styles.spacer}></Box>
				<Box xcss={isQuickSearch ? styles.scroll : undefined}>
					<AIAnswerBody />
				</Box>
			</Fragment>
		);
	}

	return (
		<DialogWrapper>
			<AIAnswerSearchBorder isLoading={false} isStreaming={false}>
				{aiAnswerContent}
				{!hasError && (
					<Box xcss={styles.sourcesContainer}>
						<AISources
							sources={commonAttributes.sources}
							searchSessionId={commonAttributes.searchSessionId}
							onClickHandler={sourceClickCallback}
							limit={MAX_SOURCES}
							enableSmartLinkSources={true}
						/>
					</Box>
				)}
				<AIAnswerFooter />
			</AIAnswerSearchBorder>
		</DialogWrapper>
	);
};
